import React, { useRef, useState, useEffect } from 'react';
import LazyLoad from 'react-lazyload';
import styled, { css } from 'styled-components';
import chunk from 'lodash.chunk';

import { AverageStars } from '../../icons';
import { media } from '../../utils/styles';
import { Rating } from '../Rating';
import { ErrorModal } from './ErrorModal';
import { log } from '../../utils/logs';

const Section = styled.div``;

const Center = styled.div`
  ${media.phone} {
    text-align: center;
  }
`;

const AverageWrapGrid = styled.div`
  display: flex;
  align-items: center;
  grid-gap: 50px;
  margin-bottom: 60px;
  ${media.phone} {
    grid-gap: 0;
    flex-wrap: wrap;
  }
`;
const AverageWrap = styled.div`
  a {
    text-decoration: none;
  }
  ${media.phone} {
    order: 2;
    display: inline-block;
    text-align: left;
    margin: 0 auto;
  }
`;

const Name = styled.div`
  font-size: 16px;
  line-height: 19px;
  color: #1c1c1c;
  flex: 1;
`;

const StyledRating = styled(Rating)`
  margin-top: 6px;
  margin-bottom: 0;
  span {
    display: none;
  }
`;

const Image = styled(LazyLoad)`
  width: 60px;
  margin-right: 20px;
  > img {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const Date = styled.time`
  font-size: 13px;
  line-height: 16px;
  color: #9caab2;
  flex-shrink: 0;
  margin-left: 15px;
`;

const Text = styled.p`
  font-size: 14px;
  line-height: 27px;
  color: #1c1c1c;
`;

const Title = styled.h4`
  font-weight: 500;
  font-size: 18px;
  line-height: 22px;
  color: #1c1c1c;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 0 0 15px;
`;

const Review = styled.article`
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  padding: 50px 0;
  &:first-of-type {
    border-top: 1px solid #e5e5e5;
  }
  ${media.phone} {
    flex-wrap: wrap;
    padding: 30px 0;
  }
`;

const Content = styled.div`
  width: 600px;
  margin-left: 30px;
  ${media.tablet} {
    width: 450px;
  }
  ${media.phone} {
    width: 100%;
    margin-top: 30px;
    margin-left: 0;
  }
`;

const SubHeading = styled.div`
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 1px;
  color: #1c1c1c;
  margin: 10px 0 0;
  img {
    margin-top: -7px;
    max-width: 80px;
    display: inline-block;
    vertical-align: middle;
  }
`;

const Average = styled.div`
  display: inline-flex;
  align-items: baseline;
  font-weight: bold;
  font-size: 26px;
  color: #1c1c1c;
  svg {
    margin-left: 5px;
  }
  span {
    margin-left: 3px;
    font-size: 14px;
    opacity: 0.5;
    font-weight: normal;
    margin-right: 6px;
  }
`;

const Heading = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: 34px;
  color: #1c1c1c;
  margin: 0 0 25px;
  ${media.phone} {
    text-align: center;
  }
`;

const Pagination = styled.div`
  margin-top: 48px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const PageButton = styled.button`
  padding: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 18px;
  margin: 3px;
  border-radius: 4px;
  background: transparent;
  color: #000000;
  &:hover {
    background: #f2f2f2;
  }
  ${({ $active }) =>
    $active &&
    css`
      color: #fff;
      background: #000000;
    `}
`;

const NextButton = styled.button`
  padding: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 20px;
  position: relative;
  bottom: -2px;
`;

const PrevButton = styled(NextButton)`
  & {
    margin-left: 0;
    margin-right: 20px;
  }

  > svg {
    transform: rotate(180deg);
  }
`;

const AddReviewButton = styled.button`
  margin-left: auto;
  width: 204px;
  height: 58px;
  padding: 0;
  background: transparent;
  border: 1px solid #e1e5e8;
  border-radius: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  color: #101010;
  &:hover {
    border-color: #101010;
  }
  ${media.phone} {
    order: 1;
    width: calc(100% - 60px);
    margin: 0 auto 40px;
  }
`;

export const CustomerReviews = ({ reviews: allReviews }) => {
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const initialLoadRef = useRef(true);
  const reviewsRef = useRef(null);

  useEffect(() => {
    if (initialLoadRef.current || !reviewsRef.current) {
      initialLoadRef.current = false;
      return;
    }
    window.scroll({
      behavior: 'smooth',
      left: 0,
      top: reviewsRef.current.offsetTop - 150,
    });
  }, [page]);

  const chunks = chunk(allReviews, 8);

  const totalPages = chunks.length;

  const reviews = chunks[page - 1];

  const renderPagination = () => {
    return chunks.map((_, index) => (
      <PageButton onClick={() => setPage(index + 1)} $active={index + 1 === page} key={index}>
        {index + 1}
      </PageButton>
    ));
  };

  const onNext = () => {
    setPage(prev => prev + 1);
  };

  const onPrev = () => {
    setPage(prev => prev - 1);
  };

  const onAddReview = () => {
    log('click', {
      category: 'engagement',
      label: 'write_a_review',
    });
    setShowModal(true);
  };

  return (
    <Section ref={reviewsRef}>
      {showModal && <ErrorModal onClose={() => setShowModal(false)} />}
      <Heading>Customer reviews</Heading>
      <Center>
        <AverageWrapGrid>
          <AverageWrap>
            <Average>
              4.9
              <span>/5</span>
              <AverageStars />
            </Average>
            <SubHeading>AVERAGE RATING</SubHeading>
          </AverageWrap>
          <AverageWrap>
            <a href="https://www.trustpilot.com/review/reshape.me" target="_blank" rel="noreferrer">
              <Average>
                4.5<span>/5</span>
                <img src="/img/trustpilot-stars-4.5.svg" width="90" alt="Trustpilot rating" />
              </Average>
              <SubHeading>
                Excellent on{' '}
                <img src="/img/trustpilot-logo.svg" width="90" alt="Trustpilot rating" />
              </SubHeading>
            </a>
          </AverageWrap>
          <AddReviewButton onClick={onAddReview}>Write a Review</AddReviewButton>
        </AverageWrapGrid>
      </Center>
      <div>
        {reviews.map((review, index) => (
          <Review key={index}>
            <Author>
              <Image once>
                {review.avatar.fluid ? (
                  <img
                    src={review.avatar.fluid.src}
                    srcSet={review.avatar.fluid.srcSet}
                    alt={review.avatar.alt}
                  />
                ) : (
                  <img src="/img/avatar.svg" alt="avatar" />
                )}
              </Image>
              <Name>
                <div>{review.name}</div>
                <StyledRating rate={review.rating} />
              </Name>
            </Author>
            <Content>
              <Header>
                <Title>{review.title1}</Title>
                <Date>{review.date}</Date>
              </Header>
              <Text>{review.text}</Text>
            </Content>
          </Review>
        ))}
      </div>
      <Pagination>
        {page > 1 && (
          <PrevButton onClick={onPrev}>
            <svg
              width="11"
              height="19"
              viewBox="0 0 11 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.45972 19C1.86635 19 2.18957 18.8525 2.47109 18.5785L10.4682 10.6855C10.8227 10.3272 10.9896 9.9584 11 9.50527C11 9.05213 10.8332 8.67277 10.4682 8.32501L2.47109 0.42152C2.18957 0.147532 1.85592 0 1.45972 0C0.646446 0 0 0.653355 0 1.46478C0 1.86522 0.166825 2.24459 0.458768 2.53966L7.54882 9.50527L0.458768 16.4603C0.166825 16.7554 0 17.1348 0 17.5352C0 18.3466 0.646446 19 1.45972 19Z"
                fill="#222222"
              />
            </svg>
          </PrevButton>
        )}
        {renderPagination()}
        {page !== totalPages && (
          <NextButton onClick={onNext}>
            <svg
              width="11"
              height="19"
              viewBox="0 0 11 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.45972 19C1.86635 19 2.18957 18.8525 2.47109 18.5785L10.4682 10.6855C10.8227 10.3272 10.9896 9.9584 11 9.50527C11 9.05213 10.8332 8.67277 10.4682 8.32501L2.47109 0.42152C2.18957 0.147532 1.85592 0 1.45972 0C0.646446 0 0 0.653355 0 1.46478C0 1.86522 0.166825 2.24459 0.458768 2.53966L7.54882 9.50527L0.458768 16.4603C0.166825 16.7554 0 17.1348 0 17.5352C0 18.3466 0.646446 19 1.45972 19Z"
                fill="#222222"
              />
            </svg>
          </NextButton>
        )}
      </Pagination>
    </Section>
  );
};
